import { useSearchParams } from "next/navigation";
import { useCartStore } from "@/features/cart/components";
import { useCartStoreScope } from "@/features/cart/components/cart-store-scope-provider";
import type { CartStore } from "@/features/cart/store/cart-store";
import type { CartStoreType } from "@/features/cart/types/cart-store-type";
import { useExtendDietCartStoreSafe } from "@/features/extend-diet/components/extend-diet-cart-provider";
import type { ExtendDietCartStore } from "@/features/extend-diet/store/extend-diet-cart-store";
import { useMultidietCartStore } from "@/features/multidiet/components/multidiet-cart-provider";
import type { MultidietCartStore } from "@/features/multidiet/store/multidiet-cart-store";
import { useReorderCartStoreSafe } from "@/features/reorder/components/reorder-cart-provider";
import type { ReorderCartStore } from "@/features/reorder/store/reorder-cart-store";

export const useCartStoreService = ({
  type,
}: {
  type?: CartStoreType | null;
}) => {
  const searchParams = useSearchParams();
  const scopeType = useCartStoreScope();
  const paramType = type ?? scopeType ?? searchParams.get("type");
  const baseCartStore = useCartStore((state) => state);
  const multidietCartStore = useMultidietCartStore((state) => state);
  const extendDietCartStore = useExtendDietCartStoreSafe((state) => state);
  const reorderCartStore = useReorderCartStoreSafe((state) => state);

  const isMultidiet =
    multidietCartStore.cartId && paramType && paramType === "multidiet";
  const isExtendDiet =
    paramType === "extend-diet" && extendDietCartStore !== null;
  const isReorder = paramType === "reorder" && reorderCartStore !== null;

  let cartStore:
    | CartStore
    | MultidietCartStore
    | ExtendDietCartStore
    | ReorderCartStore;

  if (isExtendDiet && extendDietCartStore) {
    cartStore = extendDietCartStore;
  } else if (isReorder && reorderCartStore) {
    cartStore = reorderCartStore;
  } else if (isMultidiet) {
    cartStore = multidietCartStore;
  } else {
    cartStore = baseCartStore;
  }

  return { cartStore, isMultidiet, isExtendDiet: !!isExtendDiet, isReorder };
};
